import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ActionDialog } from "@/components/dashboard/ActionDialog";
import { ClearArea } from "@/components/admin/Purge";
import { Plus, Pause, Play, Trash2, Network } from "lucide-react";
import { toast } from "sonner";

export default function Buyers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const refresh = () => api.get("/admin/buyers").then(r => setRows(r.data || []));
  useEffect(() => { refresh(); }, []);

  const toggleLive = async (b) => {
    const wasLive = b.live === true || b.live === "true";
    try {
      await api.patch(`/admin/buyers/${b.id}`, { live: !wasLive });
      toast.success(wasLive ? "Buyer paused" : "Buyer set live");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update buyer"); }
  };

  const deleteBuyer = async (b) => {
    if (!window.confirm(`Delete buyer "${b.name}"? Routing will exclude them immediately. This can't be undone.`)) return;
    try {
      await api.delete(`/admin/buyers/${b.id}`);
      toast.success(`${b.name} deleted`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete buyer"); }
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="Buyer panel" subtitle={`${rows.length} ${rows.length === 1 ? "buyer" : "buyers"} across all sectors.`} actions={<div className="flex items-center gap-2"><ClearArea area="buyers" onDone={refresh} label="Clear buyers" /><Button className="rounded-full" onClick={() => setOpen(true)} data-testid="admin-new-buyer"><Plus className="size-4" /> Add buyer</Button></div>} />

      <ActionDialog
        open={open}
        onOpenChange={setOpen}
        title="Add a buyer"
        description="Onboard a new buyer onto the platform. They'll appear in routing after activation."
        endpoint="/admin/buyers"
        submitLabel="Add buyer"
        testidPrefix="buyer"
        onSuccess={refresh}
        fields={[
          { name: "name", label: "Company name", required: true, placeholder: "e.g. Sterling Mortgage Co.", type: "text" },
          { name: "sectors", label: "Sectors (comma-separated slugs)", required: true, placeholder: "mortgages, debt-help", type: "csv", help: "Use slug form: mortgages, debt-help, business-finance, life-insurance, claims-management, wealth-management, commercial-finance, property-investment, payday-loans, investor-leads" },
          { name: "monthly_cap", label: "Monthly cap (leads)", type: "number", required: true, defaultValue: 1000, min: 100 },
          { name: "live", label: "Status", type: "select", defaultValue: "true", options: [{ value: "true", label: "Live" }, { value: "false", label: "Paused" }] },
        ]}
      />

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="buyers-empty">
              <div className="inline-grid place-items-center size-12 rounded-xl bg-slate-50 mb-4"><Network className="size-5 text-slate-400" /></div>
              <h3 className="text-base font-semibold text-slate-900">No buyers yet</h3>
              <p className="mt-1 text-sm text-slate-500">Add your first buyer to start routing inbound leads through them.</p>
              <Button className="rounded-full mt-5" onClick={() => setOpen(true)}><Plus className="size-4" /> Add buyer</Button>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60"><tr className="text-xs uppercase tracking-widest text-slate-500 text-left">
                <th className="py-3 px-4 font-medium">Buyer</th>
                <th className="font-medium">Sectors</th>
                <th className="font-medium">Status</th>
                <th className="font-medium">Acceptance</th>
                <th className="font-medium pr-4">Monthly cap</th>
                <th className="font-medium text-right pr-4 w-32"></th>
              </tr></thead>
              <tbody>
                {rows.filter(b => b && b.id).map((b) => {
                  const isLive = b.live === true || b.live === "true";
                  const idShort = (b.id || "").slice(0, 8);
                  return (
                    <tr key={b.id} className="group border-t border-slate-100 hover:bg-slate-50/40" data-testid={`buyer-row-${idShort}`}>
                      <td className="py-3 px-4 font-medium text-slate-900">{b.name || "—"}</td>
                      <td><div className="flex flex-wrap gap-1.5">{(b.sectors || []).map(s => <span key={s} className="text-[10px] capitalize rounded border border-slate-200 bg-white px-2 py-0.5 text-slate-600">{s.replace("-", " ")}</span>)}</div></td>
                      <td><span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${isLive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{isLive ? "Live" : "Paused"}</span></td>
                      <td className="w-48"><div className="flex items-center gap-3"><Progress value={Math.round((b.acceptance || 0.65) * 100)} className="h-1.5" /><span className="text-xs font-mono text-slate-700">{Math.round((b.acceptance || 0.65) * 100)}%</span></div></td>
                      <td className="font-mono tabular-nums">{Number(b.monthly_cap).toLocaleString()}</td>
                      <td className="text-right pr-4">
                        <div className="inline-flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-500 hover:text-slate-900" onClick={() => toggleLive(b)} data-testid={`buyer-toggle-${idShort}`} title={isLive ? "Pause buyer" : "Set live"}>
                            {isLive ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-400 hover:text-rose-600" onClick={() => deleteBuyer(b)} data-testid={`buyer-delete-${idShort}`} title="Delete buyer">
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
